import React, { useCallback } from 'react';
import type { GoodsOption } from 'shared/src/api';
import { Flex, List, PrimaryButton, VerticalSpacing } from '@banhala/ui-kit';
import { PricingListItem } from './PricingListItem';
import { PricingInfo } from './PricingInfo';
import { useProductCheckout } from '../../queries/useProductCheckout';
import { bridge } from 'shared/src/utils';
import { useAddToCart } from '../../queries/useAddToCart';
import { useIsBottomSheetGiftMode } from '../../hooks/useIsBottomSheetGiftMode';
import { useLogClickAddToCart } from '../../hooks/useLogClickAddToCart';
import { useLogClickCheckOut } from '../../hooks/useLogClickCheckOut';
import { useMyInfo } from 'shared/src/queries';

export const PricingListView: React.FC<{
  selected: GoodsOption[];
  onUpdateOption: (option: GoodsOption) => void;
  onSelectMore: () => void;
  onPressDelete: (option: GoodsOption) => void;
  onCompleteAddToCart: () => void;
  onCompletePurchase: () => void;
  onAnonymouseUserPressedPurchase: () => void;
}> = props => {
  const { mutateAsync: checkoutProduct, isPending: loadingProductCheckout } =
    useProductCheckout(props.selected);
  const { mutateAsync: addToCart, isPending: loadingAddToCart } = useAddToCart(
    props.selected,
  );
  const { data: myInfo } = useMyInfo();

  const logClickAddToCart = useLogClickAddToCart({ selected: props.selected });
  const logClickCheckOut = useLogClickCheckOut({ selected: props.selected });

  const onPressPurchase = useCallback(async () => {
    //비회원일때 회원가입 모달 띄우기
    if (myInfo?.is_anonymous) {
      props.onAnonymouseUserPressedPurchase();
      return;
    }
    logClickCheckOut();
    const result = await checkoutProduct();

    if (result) {
      bridge.openDeeplink(result.deeplink);
      props.onCompletePurchase();
    }
  }, [checkoutProduct, logClickCheckOut, myInfo?.is_anonymous, props]);

  const onPressAddToCart = useCallback(async () => {
    logClickAddToCart();
    await addToCart();

    bridge.updateMemberMe(JSON.stringify({ refetch: false }));
    props.onCompleteAddToCart();
  }, [addToCart, logClickAddToCart, props]);
  const isGiftMode = useIsBottomSheetGiftMode();

  return (
    <Flex flex={1}>
      <List<GoodsOption>
        ListHeaderComponent={
          isGiftMode ? undefined : (
            <Flex>
              <PrimaryButton
                variant={'fill'}
                outline
                size={'large'}
                onPress={props.onSelectMore}
              >
                옵션 선택하기
              </PrimaryButton>
              <VerticalSpacing size={12} />
            </Flex>
          )
        }
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingVertical: 16,
          paddingHorizontal: 16,
        }}
        data={props.selected}
        ItemSeparatorComponent={() => <VerticalSpacing size={12} />}
        renderItem={({ item }) => {
          return (
            <PricingListItem
              option={item}
              onUpdateOption={props.onUpdateOption}
              onPressDelete={() => props.onPressDelete(item)}
            />
          );
        }}
      />
      <PricingInfo selected={props.selected} />
      {isGiftMode ? (
        <Flex.Row
          padding={{ horizontal: 12, vertical: 12 }}
          borderColor={'border_tertiary'}
          borderTopWidth={1}
          gap={8}
        >
          <Flex flex={1}>
            <PrimaryButton
              variant={'fill'}
              size={'xlarge'}
              onPress={onPressPurchase}
              state={loadingProductCheckout ? 'loading' : undefined}
            >
              선물하기
            </PrimaryButton>
          </Flex>
        </Flex.Row>
      ) : (
        <Flex.Row
          padding={{ horizontal: 12, vertical: 12 }}
          borderColor={'border_tertiary'}
          borderTopWidth={1}
          gap={8}
        >
          <Flex flex={1}>
            <PrimaryButton
              variant={'fill'}
              size={'xlarge'}
              outline={true}
              onPress={onPressAddToCart}
              state={loadingAddToCart ? 'loading' : undefined}
            >
              장바구니
            </PrimaryButton>
          </Flex>
          <Flex flex={1}>
            <PrimaryButton
              variant={'fill'}
              size={'xlarge'}
              onPress={onPressPurchase}
              state={loadingProductCheckout ? 'loading' : undefined}
            >
              구매하기
            </PrimaryButton>
          </Flex>
        </Flex.Row>
      )}
    </Flex>
  );
};
